import AutoComplete from 'components/Common/AutoComplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlowRender from 'pages/Flow/FlowRender';
import FlowRunForm from 'pages/Flow/FlowRun/FlowRunForm';
import FlowRunMacroTable from 'pages/Flow/FlowRun/FlowRunMacroTable';
import MotifStepIcon from 'BaseComponents/MotifStepIcon';
import PermissionService from 'components/Common/PermissionService';
import React, { useContext, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import debounce from 'lodash/debounce';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip,
} from '@mui/material';
import {
  BillableSourceType,
  useGetBillableConfig,
  useUpsertBillableConfig,
} from 'api/billableConfigs';
import { DEFAULT_PAUSE_LEVEL_VALUE } from 'redux/reducer/FlowReducer/constants';
import { FlowCanvas } from '../FlowBuilder/FlowBuilderLayout';
import { FlowContext } from 'pages/Flow/FlowContextProvider';
import { FlowRunContext } from 'pages/Flow/FlowRun/FlowRunProvider';
import { HSpinner } from 'BaseComponents';
import { MotfiFullScreenDialogTitle } from 'BaseComponents/MotifFullScreenDialog/MotfiFullScreenDialogTitle';
import { MotifFullScreenDialog } from 'BaseComponents/MotifFullScreenDialog/MotifFullScreenDialog';
import { MotifFullScreenDialogActions } from 'BaseComponents/MotifFullScreenDialog/MotifFullScreenDialogActions';
import { MotifFullScreenDialogContent } from 'BaseComponents/MotifFullScreenDialog/MotifFullScreenDialogContent';
import { WarningDialog } from 'components/CleanRooms/CrManagement/AWSAccountSettings/WarningDialog';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import { canSeeBillableFeatures } from 'utils/permissionUtils';
import {
  clearFlowNodesCompleteDetails,
  clearIncludedNodesByBranches,
  selectPauseLevelForCachedRun,
  setFlowDetailsSuccess,
  setIncludedNodesByBranches,
} from 'redux/actions/FlowAction';
import { productPerm } from 'utils/appConstants';
import { useAllNodeTags } from 'hooks/useAllNodeTags';
import { useDispatch, useSelector } from 'react-redux';
import {
  useFetchFlowDetails,
  useGetCRFlowRunParametersAndMappings,
  useListFlowNodesByBranches,
  useSubmitFlowRun,
} from 'api/flows';
import { useGetDPAccountantView } from 'api/cleanrooms/useGetDPAccountantView';
import { useParams } from 'react-router-dom';


const FlowRunDialog = () => {
  const { crId } = useParams();
  const { dataContextRef } = useContext(FlowContext);
  const { isOpen, closeModal, modalDetails } = React.useContext(FlowRunContext);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [runDetails, setRunDetails] = React.useState({ name: '', flowParameters: [] });
  const [transformedData, setTransformedData] = React.useState([]);
  const [macroData, setMacroData] = React.useState([]);
  const [pauseLevelID, setPauseLevelID] = React.useState(DEFAULT_PAUSE_LEVEL_VALUE);
  const [branches, setBranches] = React.useState('');
  const [excludeNodesWithoutTags, setExcludeNodesWithoutTags] = React.useState(false);
  const [ignoreParentChildRule, setIgnoreParentChildRule] = React.useState(false);
  const [includeMismatchingTaggedNodes, setIncludeMismatchingTaggedNodes] = React.useState(false);
  const [openIgnoreParentChildWarning, setOpenIgnoreParentChildWarning] = React.useState(false);
  const [hasConfirmedIgnoreParentChild, setHasConfirmedIgnoreParentChild] = React.useState(false);
  const [showBranchingSettings, setShowBranchingSettings] = React.useState(false);
  const [step, setStep] = useState(0);
  const [isBillable, setIsBillable] = React.useState(false);
  const { ID: orgID } = useSelector(activeOrgSelector);
  const crDetail = useSelector((state) => state.cleanRooms.detail);
  const isFlowNodesAndEdgesLoaded = useSelector((state) => state.flow.flowNodesAndEdgesLoaded);
  const includedNodeIds = useSelector((state) => state.flow.includedNodeIds);
  const dispatch = useDispatch();
  const flow = useSelector(state => state.flow.flow);
  const allNodeTags = useAllNodeTags();
  const { mutate: listNodesByBranches } = useListFlowNodesByBranches();

  const isRunWithoutBranches = React.useMemo(() => (branches.trim() === ''), [branches]);

  const fetchNodesByBranches = React.useCallback(() => {
    if (isRunWithoutBranches) {
      dispatch(clearIncludedNodesByBranches());
      return;
    }

    const payload = {
      cleanRoomID: crId,
      cleanRoomFlowID: modalDetails?.ID,
      branches: branches.trim(),
      flowVersion: flow?.version,
    };

    if (excludeNodesWithoutTags) {
      payload.excludeNodesWithoutTags = excludeNodesWithoutTags;
    }

    if (ignoreParentChildRule) {
      payload.ignoreParentChildRule = ignoreParentChildRule;
    }

    if (includeMismatchingTaggedNodes) {
      payload.includeMismatchingTaggedNodes = includeMismatchingTaggedNodes;
    }

    if (
      pauseLevelID !== DEFAULT_PAUSE_LEVEL_VALUE
      && pauseLevelID !== null
      && pauseLevelID !== undefined
    ) {
      payload.pauseLevelID = `Level-${pauseLevelID}`;
    }

    listNodesByBranches(payload, {
      onSuccess: (data) => {
        if (data?.data?.nodeIDs) {
          dispatch(setIncludedNodesByBranches(data.data.nodeIDs));
        }
        else {
          dispatch(clearIncludedNodesByBranches());
        }
      },
    });
  }, [
    dispatch,
    crId,
    modalDetails?.ID,
    flow,
    listNodesByBranches,
    branches,
    excludeNodesWithoutTags,
    ignoreParentChildRule,
    includeMismatchingTaggedNodes,
    isRunWithoutBranches,
    pauseLevelID,
  ]);

  const debouncedFetchNodesByBranches = React.useMemo(
    () => debounce(fetchNodesByBranches, 500),
    [fetchNodesByBranches],
  );

  React.useEffect(() => {
    if (isRunWithoutBranches) {
      setExcludeNodesWithoutTags(false);
    }
  }, [isRunWithoutBranches]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    debouncedFetchNodesByBranches();
    return () => debouncedFetchNodesByBranches.cancel();
  }, [
    branches,
    excludeNodesWithoutTags,
    ignoreParentChildRule,
    debouncedFetchNodesByBranches,
    isOpen,
  ]);



  React.useEffect(() => {
    if (!ignoreParentChildRule) {
      setHasConfirmedIgnoreParentChild(false);
    }
  }, [ignoreParentChildRule]);

  const isDifferentialPrivacyEnabled = PermissionService.isProductEnabled([
    productPerm.DifferentialPrivacy,
  ]);

  const handleModalClose = React.useCallback(() => {
    closeModal();

    // Reset React Redux states
    dispatch(clearIncludedNodesByBranches());
    dispatch(clearFlowNodesCompleteDetails());
    dispatch(setFlowDetailsSuccess({
      flowNodesAndEdgesLoaded: false,
      flow: { edges: [], nodes: [] },
    }));

    // Reset local states
    setMacroData([]);
    setTransformedData([]);
    setIsFormValid(false);
    setRunDetails({ name: '', flowParameters: [] });
    setPauseLevelID(DEFAULT_PAUSE_LEVEL_VALUE);
    setBranches('');
    setExcludeNodesWithoutTags(false);
    setIgnoreParentChildRule(false);
    setOpenIgnoreParentChildWarning(false);
    setHasConfirmedIgnoreParentChild(false);
    setStep(0);
    setIsBillable(false);
  }, [closeModal, dispatch]);

  const {
    isError: isFlowRunParamMappingError,
    isSuccess: isFlowRunParamMappingSuccess,
    isLoading: isFlowRunParamMappingLoading,
    data: flowRunParametersMapping,
  } = useGetCRFlowRunParametersAndMappings({
    crFlowId: modalDetails?.ID,
    crId,
    enabled: isOpen && !!modalDetails?.ID,
  });

  const {
    isSuccess: flowDetailsSuccess,
    isFetching: flowDetailsFetching,
    data: flowDetails,
  } = useFetchFlowDetails({
    crId,
    crFlowId: modalDetails?.ID,
    enabled: isOpen && !!modalDetails?.ID,
  });


  const pauseLevels = useMemo(() => {
    const levels = flowRunParametersMapping?.data?.levels || [];
    return [...levels].sort((a, b) => a - b);
  }, [flowRunParametersMapping?.data?.levels]);

  const { refetch: refetchBudgetAccountant } = useGetDPAccountantView(orgID, crId, false);

  const canSeeBillableCheckbox = canSeeBillableFeatures(crDetail?.type);

  // Get flow billable config
  const {
    data: flowBillableConfig,
    isFetching: isLoadingFlowBillableConfig,
  } = useGetBillableConfig(
    modalDetails?.ID,
    BillableSourceType.BILLABLE_FLOW,
    !!modalDetails?.ID && isOpen && canSeeBillableCheckbox,
  );

  const {
    mutate: upsertBillableConfig,
    isLoading: isUpsertingBillableConfig,
  } = useUpsertBillableConfig();

  // Get cleanroom billable config for inheritance
  const {
    data: cleanRoomBillableConfig,
  } = useGetBillableConfig(
    crId,
    BillableSourceType.BILLABLE_CR,
    !!crId && isOpen && canSeeBillableCheckbox,
  );

  const {
    mutate,
    isSuccess: flowRunAPISuccess,
    reset: resetFlowRunMutation,
    isLoading: loading,
    data: flowRunResponse,
  } = useSubmitFlowRun({ flowId: modalDetails?.ID, crId });

  const shouldShowBillableCheckbox = React.useMemo(() => {
    if (!canSeeBillableCheckbox) {
      return false;
    }

    // Check Flow config first (highest priority)
    if (flowBillableConfig) {
      return flowBillableConfig.value === 'true';
    }

    // Check cleanroom billable config if no Flow config
    return cleanRoomBillableConfig?.value === 'true';
  }, [canSeeBillableCheckbox, flowBillableConfig, cleanRoomBillableConfig]);

  // Default value is always true when checkbox is shown (since we only show when config is true)
  const defaultBillableValue = shouldShowBillableCheckbox;

  React.useEffect(() => {
    setIsBillable(defaultBillableValue);
  }, [defaultBillableValue]);

  const isSubmitDisabled = useMemo(
    () => loading || !isFormValid || isFlowRunParamMappingError || isUpsertingBillableConfig,
    [loading, isFormValid, isFlowRunParamMappingError, isUpsertingBillableConfig]);

  const isNextDisabled = runDetails.name.trim() === '';


  const processFlowRunParameters = React.useCallback((flowRunParameters) => {
    const macros = [];
    const runParametersWithoutMacros = [];
    const macroIds = new Set();

    // Filter parameters based on included node IDs from branches selection
    const filteredFlowRunParameters = flowRunParameters.filter((flowRunParam) => {
      // If no branches are selected (empty includedNodeIds), show all parameters
      if (!includedNodeIds || includedNodeIds.length === 0) {
        return true;
      }
      // Only include parameters for nodes that are in the included node IDs
      return includedNodeIds.includes(flowRunParam.nodeID);
    });

    filteredFlowRunParameters.forEach((flowRunParam) => {
      const nodeRunParamsWithoutMacros = {
        nodeID: flowRunParam.nodeID,
        nodeQuestionName: flowRunParam.nodeQuestionName,
        nodeRunParameters: [],
      };

      flowRunParam.nodeRunParameters.forEach((nodeRunParam) => {
        // TODO: When adding support for dataset materialization in Flows, remove this below
        // Filter out internal rolling parameters (dataset materialization)
        if (nodeRunParam.name && nodeRunParam.name.endsWith('_rolling')) {
          return;
        }

        // Create a new object to avoid reassigning the parameter
        const newNodeRunParam = { ...nodeRunParam, value: '' };

        // Special check for START_DATE and END_DATE
        if (!newNodeRunParam.ID) {
          if (newNodeRunParam.name === 'START_DATE') {
            newNodeRunParam.ID = 'START_DATE';
          }
          else if (newNodeRunParam.name === 'END_DATE') {
            newNodeRunParam.ID = 'END_DATE';
          }
        }

        if (newNodeRunParam.macro) {
          if (!macroIds.has(newNodeRunParam.macro.ID)) {
            const macroWithMapping = {
              ...newNodeRunParam.macro,
              runParamMapping: [],
            };
            macros.push(macroWithMapping);
            macroIds.add(newNodeRunParam.macro.ID);
          }
          const macro = macros.find((m) => m.ID === newNodeRunParam.macro.ID);
          macro.runParamMapping.push({
            nodeID: flowRunParam.nodeID,
            runParamID: newNodeRunParam.ID,
            runParamName: newNodeRunParam.name,
          });
        }
        else {
          nodeRunParamsWithoutMacros.nodeRunParameters.push(newNodeRunParam);
        }
      });

      // Avoid Empty Entries: Only add nodeRunParameters if it has at least one entry
      if (nodeRunParamsWithoutMacros.nodeRunParameters.length > 0) {
        runParametersWithoutMacros.push(nodeRunParamsWithoutMacros);
      }
    });

    return { macros, runParametersWithoutMacros };
  }, [includedNodeIds]);

  React.useEffect(() => {
    if (flowDetailsSuccess && !flowDetailsFetching && flowDetails?.data?.cleanRoomFlow) {
      dispatch(setFlowDetailsSuccess({
        ...flowDetails.data.cleanRoomFlow,
        flowNodesAndEdgesLoaded: true,
      }));
    }
  }, [dispatch, flowDetailsSuccess, flowDetailsFetching, flowDetails]);

  React.useEffect(() => {
    if (isFlowRunParamMappingSuccess && flowRunParametersMapping) {
      const res = processFlowRunParameters(flowRunParametersMapping?.data?.flowRunParameters);
      setMacroData(res.macros);
      setTransformedData(res.runParametersWithoutMacros);
    }
  }, [
    flowRunParametersMapping,
    isFlowRunParamMappingSuccess,
    includedNodeIds,
    processFlowRunParameters,
  ]);

  const areAllMacrosValid = (m) => m.every((macro) => macro.value && macro.value.trim() !== '');

  const isAllTransformedDataValid = (d) => d.every((item) => item.nodeRunParameters.every((param) => param.value && param.value.trim() !== ''),
  );

  React.useEffect(() => {
    let isValid = runDetails.name !== '' && isAllTransformedDataValid(transformedData);
    isValid = isValid && areAllMacrosValid(macroData);
    setIsFormValid(isValid);
  }, [macroData, transformedData, runDetails]);

  // Handle billable config after flow run creation
  React.useEffect(() => {
    if (flowRunAPISuccess && flowRunResponse) {
      const flowRunId = flowRunResponse?.cleanRoomFlowRun?.ID;

      // Handle billable config if checkbox was shown and user unchecked it
      if (shouldShowBillableCheckbox && !isBillable && flowRunId) {
        const billableConfig = {
          sourceID: flowRunId,
          sourceType: BillableSourceType.BILLABLE_FLOW_RUN,
          value: 'false',
        };

        upsertBillableConfig(billableConfig);
      }

      // Reset and close modal
      resetFlowRunMutation();
      setRunDetails({ name: '', flowParameters: [] });
      dataContextRef.current.refreshListData();
      if (isDifferentialPrivacyEnabled && crDetail?.cleanRoomPrivate) {
        refetchBudgetAccountant();
      }
      handleModalClose();
    }
  }, [
    flowRunAPISuccess,
    flowRunResponse,
    shouldShowBillableCheckbox,
    isBillable,
    upsertBillableConfig,
    resetFlowRunMutation,
    dataContextRef,
    isDifferentialPrivacyEnabled,
    crDetail?.cleanRoomPrivate,
    refetchBudgetAccountant,
    handleModalClose,
  ]);

  if (modalDetails === null) return null;

  const handleSubmit = () => {
    // Transform transformedData into a map for quick lookup by nodeID
    const transformedDataMap = new Map(
      transformedData.map((item) => [
        item.nodeID,
        Object.fromEntries(
          item.nodeRunParameters.map((param) => [
            param.name,
            {
              value: param.value,
              parameterType: param.parameterType,
            },
          ]),
        ),
      ]),
    );

    // Transform macroData into a map for quick lookup by nodeID
    const macroDataMap = new Map();
    macroData.forEach((macro) => {
      macro.runParamMapping.forEach((mapping) => {
        if (!macroDataMap.has(mapping.nodeID)) {
          macroDataMap.set(mapping.nodeID, {});
        }
        macroDataMap.get(mapping.nodeID)[mapping.runParamName] = {
          value: macro.value,
        };
      });
    });

    // Merge the data from both maps
    const mergedData = Array.from(
      new Set([...transformedDataMap.keys(), ...macroDataMap.keys()]),
    ).map((nodeID) => ({
      nodeID,
      parameters: { ...transformedDataMap.get(nodeID), ...macroDataMap.get(nodeID) },
    }));

    const payload = { cleanRoomFlowRun: { ...runDetails, flowParameters: mergedData } };

    if (pauseLevelID !== DEFAULT_PAUSE_LEVEL_VALUE) {
      payload.cleanRoomFlowRun.pauseLevelID = `Level-${pauseLevelID}`;
    }

    if (branches.trim()) {
      payload.cleanRoomFlowRun.branches = branches.trim();
    }

    if (excludeNodesWithoutTags) {
      payload.cleanRoomFlowRun.excludeNodesWithoutTags = excludeNodesWithoutTags;
    }

    if (ignoreParentChildRule) {
      payload.cleanRoomFlowRun.ignoreParentChildRule = ignoreParentChildRule;
    }

    if (includeMismatchingTaggedNodes) {
      payload.cleanRoomFlowRun.includeMismatchingTaggedNodes = includeMismatchingTaggedNodes;
    }

    mutate(payload);
  };

  const handleNodeRunParamFormFieldChange = ({ e, nodeID, nodeParam }) => {
    const { value } = e.target;
    const { ID: selectedParamID, name: selectedParamName } = nodeParam;
    const updatedData = transformedData.map((item) => {
      if (item.nodeID === nodeID) {
        const updatedNodeRunParameters = item.nodeRunParameters.map((param) => {
          const enabledStartDate = selectedParamName === 'START_DATE' && param.name === 'START_DATE';
          const enabledEndDate = selectedParamName === 'END_DATE' && param.name === 'END_DATE';
          const enabledDateParameters = selectedParamName === 'START_DATE' || selectedParamName === 'END_DATE';

          if (param.ID === selectedParamID) {
            if (enabledStartDate && enabledDateParameters) {
              return { ...param, value };
            }

            if (enabledEndDate && enabledDateParameters) {
              return { ...param, value };
            }

            if (!enabledEndDate && !enabledStartDate && !enabledDateParameters) {
              return {
                ...param,
                value,
              };
            }
          }

          return param;
        });

        return {
          ...item,
          nodeRunParameters: updatedNodeRunParameters,
        };
      }
      return item;
    });

    setTransformedData(updatedData);
  };
  const updateMacroValue = (macros, macroID, newValue) => macros.map((macro) => {
    if (macro.ID === macroID) {
      return { ...macro, value: newValue };
    }
    return macro;
  });

  const handleUpdateMacroValue = (macroID, newValue) => {
    setMacroData((prevMacros) => updateMacroValue(prevMacros, macroID, newValue));
  };

  const showPauseLevelSelector = !!pauseLevels.length
    && pauseLevels.length >= 1;

  const handleConfirmIgnoreParentChild = () => {
    setOpenIgnoreParentChildWarning(false);
    setHasConfirmedIgnoreParentChild(true);
    setStep((s) => s + 1);
  };

  const handleCancelIgnoreParentChild = () => {
    setOpenIgnoreParentChildWarning(false);
  };

  const handleNextClick = () => {
    if (ignoreParentChildRule && !hasConfirmedIgnoreParentChild) {
      setOpenIgnoreParentChildWarning(true);
      return;
    }
    setStep((s) => s + 1);
  };

  return (
    <MotifFullScreenDialog
      open={isOpen}
      onClose={handleModalClose}
      anchor='bottom'
      style={{ margin: '6.25rem 0px 0px' }}
    >
      <MotfiFullScreenDialogTitle style={{ paddingLeft: '0px' }} onClose={handleModalClose}>
        <Typography variant='h3'>New Flow Run</Typography>
      </MotfiFullScreenDialogTitle>
      <MotifFullScreenDialogContent style={{ paddingTop: 0 }}>
        <Grid container justifyContent='space-between' spacing={1} gridArea='one'>
          <Grid item xs={3} pt='22px !important'>
            <Stepper
              orientation='vertical'
              activeStep={step}
              connector={<StepConnector />}
              style={{ padding: 0 }}
            >
              <Step>
                <StepLabel StepIconComponent={MotifStepIcon}>Run Details</StepLabel>
              </Step>
              <Step>
                <StepLabel StepIconComponent={MotifStepIcon}>Set Parameters</StepLabel>
              </Step>
            </Stepper>
          </Grid>
          <Grid item xs={9} pt='32px !important' pl='0px !important' sx={{ borderLeft: (theme) => `1px solid ${theme.palette.secondary.light}` }}>
            {step === 0 && (
              <Box marginLeft='30px'>
                <Typography variant='h4'>Run Details</Typography>
                <Box display='flex' gap={2} alignItems='flex-end' flexWrap='wrap' sx={{ my: 2 }}>
                  <TextField
                    data-testid='report-flow-text-field'
                    label='Run Name'
                    onChange={(e) => setRunDetails({
                      ...runDetails,
                      name: e.target.value,
                    })}
                    required
                    type='text'
                    value={runDetails.name || ''}
                    variant='outlined'
                    sx={{ minWidth: 200 }}
                  />
                  {showPauseLevelSelector && (
                    <FormControl sx={{ minWidth: 180 }}>
                      <Tooltip title='*All remaining levels will run if no choice is made.'>
                        <Select
                          label={DEFAULT_PAUSE_LEVEL_VALUE}
                          value={pauseLevelID}
                          onChange={(e) => {
                            setPauseLevelID(e.target.value);
                            // Update Redux state for flow visualization
                            const pauseValue = e.target.value === DEFAULT_PAUSE_LEVEL_VALUE
                              ? DEFAULT_PAUSE_LEVEL_VALUE
                              : e.target.value;
                            dispatch(selectPauseLevelForCachedRun(pauseValue));
                          }}
                        >
                          <MenuItem
                            key={DEFAULT_PAUSE_LEVEL_VALUE}
                            value={DEFAULT_PAUSE_LEVEL_VALUE}
                          >
                            {DEFAULT_PAUSE_LEVEL_VALUE}
                          </MenuItem>
                          {pauseLevels?.map((level) => (
                            <MenuItem key={`level-${level}`} value={level}>
                              Level
                              {' '}
                              {level}
                            </MenuItem>
                          ))}
                        </Select>
                      </Tooltip>
                    </FormControl>
                  )}
                  {shouldShowBillableCheckbox && (
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={isBillable}
                          onChange={(e) => setIsBillable(e.target.checked)}
                          color='primary'
                          disabled={isLoadingFlowBillableConfig}
                        />
                      )}
                      label='Billable'
                      sx={{ ml: 2 }}
                    />
                  )}
                  <Accordion
                    expanded={showBranchingSettings}
                    onChange={() => setShowBranchingSettings(!showBranchingSettings)}
                    disableGutters
                    elevation={0}
                    sx={{
                      width: '100%',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant='subtitle2'>
                        Branching Configuration (Optional)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems='flex-end'>
                        <Tooltip title={
                          !allNodeTags.length
                            ? 'No branches available to select in this flow. Add tags to nodes in your flow to enable branch selection.'
                            : 'Leave empty to run all branches, or select branch names to filter execution'
                        }
                        >
                          <Box>
                            <AutoComplete
                              multiple
                              openOnFocus
                              disabled={!allNodeTags.length}
                              options={allNodeTags.length ? allNodeTags : branches.split(',').map(b => b.trim()).filter(Boolean)}
                              value={branches.split(',').map(b => b.trim()).filter(Boolean)}
                              onChange={(_, newValue) => {
                                setBranches(newValue.join(','));
                              }}
                              label='Branch Names'
                              textFieldProps={{ variant: 'outlined', inputProps: { readOnly: true } }}
                              style={{
                                width: '250px',
                              }}
                            />
                          </Box>
                        </Tooltip>
                        <Tooltip title='Exclude nodes without tags from execution. Requires branch names to be specified for this option to be enabled'>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={excludeNodesWithoutTags}
                                onChange={(e) => setExcludeNodesWithoutTags(e.target.checked)}
                                disabled={isRunWithoutBranches}
                                size='small'
                              />
                            )}
                            label='Exclude nodes without tags'
                            slotProps={{
                              typography: {
                                variant: 'body2',
                              },
                            }}
                          />
                        </Tooltip>
                        <Tooltip title='Ignore parent-child dependency rules for normal flow runs. You need to ensure that the child nodes are configured to handle missing parent nodes. Requires branch names to be specified for this option to be enabled'>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={ignoreParentChildRule}
                                onChange={(e) => setIgnoreParentChildRule(e.target.checked)}
                                disabled={isRunWithoutBranches}
                                size='small'
                              />
                            )}
                            label='Ignore parent-child rules'
                            slotProps={{
                              typography: {
                                variant: 'body2',
                              },
                            }}
                          />
                        </Tooltip>
                        <Tooltip title='When enabled, the system includes all levels that have no nodes included in the specified branches. Requires branch names to be specified for this option to be enabled'>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={includeMismatchingTaggedNodes}
                                onChange={(e) => (
                                  setIncludeMismatchingTaggedNodes(e.target.checked)
                                )}
                                disabled={isRunWithoutBranches}
                                size='small'
                              />
                            )}
                            label='Enable level passthrough'
                            slotProps={{
                              typography: {
                                variant: 'body2',
                              },
                            }}
                          />
                        </Tooltip>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Box>
                <FlowCanvas sx={{ height: 'calc(100vh - 140px)' }}>
                  {isFlowNodesAndEdgesLoaded ? (
                    <FlowRender crId={crId} isCachedRun={false} isFlowRunDialog />
                  ) : (
                    <HSpinner />
                  )}
                </FlowCanvas>
              </Box>
            )}
            {step === 1 && (
              <Box marginLeft='30px'>
                <Typography variant='h4'>Parameters</Typography>
                <FormControl fullWidth>
                  {
                    !!macroData.length
                    && (
                      <Grid container spacing={1}>
                        <Typography variant='h3Branding' marginTop={2} marginLeft={2}>
                          Macros
                        </Typography>
                        <FlowRunMacroTable
                          error={isFlowRunParamMappingError}
                          loading={isFlowRunParamMappingLoading}
                          listOfFlowMacros={macroData}
                          onUpdateMacroValue={handleUpdateMacroValue}
                        />
                      </Grid>
                    )
                  }
                  <Grid container spacing={1}>
                    <FlowRunForm
                      flowRunTimeParameters={transformedData}
                      loading={isFlowRunParamMappingLoading}
                      runDetails={runDetails}
                      updateNodeRunParam={handleNodeRunParamFormFieldChange}
                      updateRunDetails={setRunDetails}
                    />
                  </Grid>
                </FormControl>
              </Box>
            )}
          </Grid>
        </Grid>
      </MotifFullScreenDialogContent>
      {openIgnoreParentChildWarning && (
        <WarningDialog
          open={openIgnoreParentChildWarning}
          onCancel={handleCancelIgnoreParentChild}
          onConfirm={handleConfirmIgnoreParentChild}
          title='Are you sure you want to ignore parent-child rules?'
          message={`Using this option may cause your Flow Run to fail if no parent nodes execute.\n
            Additionally, if multiple parents run simultaneously, the last one to finish will provide data to the child, which can lead to unpredictable results.\n
            Use only if you are certain at least one parent will always run, or if the child node can handle cases where no parents run.`}
          warningActionLabel='Confirm'
          maxWidth='md'
        />
      )}
      <MotifFullScreenDialogActions>
        <Button
          onClick={step === 1 ? () => setStep(0) : handleModalClose}
          color='primary'
          variant='outlined'
        >
          {step === 1 ? 'Back' : 'Cancel'}
        </Button>
        <Button
          variant='contained'
          onClick={step === 0 ? handleNextClick : handleSubmit}
          disabled={step === 0 ? isNextDisabled : isSubmitDisabled}
          endIcon={loading ? <HSpinner isButton /> : null}
        >
          {step === 0 ? 'Next' : 'Create Run'}
        </Button>
      </MotifFullScreenDialogActions>
    </MotifFullScreenDialog>
  );
};

export default FlowRunDialog;
